import { ChangeDetectorRef, Component, ElementRef, Input, NgZone, SimpleChanges, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DocumentService } from 'src/app/services/document.service';
import * as am4charts from "@amcharts/amcharts4/charts";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4sunburst from "@amcharts/amcharts4/plugins/sunburst";

@Component({
  selector: 'app-graph-documents-products-services',
  templateUrl: './graph-documents-products-services.component.html',
  styleUrl: './graph-documents-products-services.component.scss'
})
export class GraphDocumentsProductsServicesComponent {
  @Input() data: {
    _id: any,
    count: number
  }[] = [];

  @Input() loading: boolean = true;

  @Input() title: boolean = true;

  @Input() height: number = 300;

  @Input() count: number = 0;

  ready: boolean = false;

  @Input() error: boolean = false;
  @Input() noData: boolean = false;

  @Input() classes: number[] = [];

  @Input() classBalance: {
    display: boolean,
    qualification?: 'good' | 'average' | 'bad',
    classes: number[],
    insights?: {
      class: number,
      count: number,
      percent: number
    }[]
  } = {
      display: false,
      classes: [],
      insights: []
    };

  @ViewChild('graphproductsservicesdiv') chartElement?: ElementRef<HTMLElement>;
  private chart?: am4charts.TreeMap;

  constructor(
    private zone: NgZone,
    private documentService: DocumentService,
    private translate: TranslateService,
    private changeDetector: ChangeDetectorRef
  ) {

  }

  ngAfterViewInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.loading) {
      setTimeout(() => {
        this.makeChart();
      }, 100)
    }

  }

  ngOnDestroy() {
    if (this.chart) {
      this.chart.dispose();
    }
  }

  makeChart() {
    const self = this;
    this.zone.runOutsideAngular(() => {

      var chart = am4core.create(this.chartElement?.nativeElement, am4charts.TreeMap);
      chart.logo.setVisibility(false);
      chart.maxLevels = 2;
      chart.dataFields.value = "count";
      chart.dataFields.name = "name";
      chart.dataFields.children = "terms";
      chart.dataFields.color = "color";
      chart.colors.step = 2;
      // chart.layoutAlgorithm = chart.binaryTree;

      // enable navigation
      chart.navigationBar = new am4charts.NavigationBar();
      chart.zoomable = false;



      chart.data = [];
      const colorSet = new am4core.ColorSet();
      this.data.forEach((datum) => {
        const classFound = chart.data.find((d) => d.classNumber === datum._id.classNumber);
        if (classFound) {
          classFound.terms.push({ name: datum._id.label, count: datum.count })
        } else {
          chart.data.push({
            name: this.translate.instant('NICE_CLASSES.CLASS_NUMBER') + datum._id.classNumber,
            classNumber: datum._id.classNumber,
            color: (this.classBalance.display && !this.classBalance.classes.includes(datum._id.classNumber)) ? am4core.color("#9A9A9A") : colorSet.next(),
            terms: [
              {
                name: datum._id.label,
                count: datum.count
              }
            ]
          })
        }
      })

      // level 0 series template
      var level0SeriesTemplate = chart.seriesTemplates.create("0");
      let level0_column = level0SeriesTemplate.columns.template;
      level0_column.column.cornerRadius(10, 10, 10, 10);
      level0_column.propertyFields.fill = "color";
      level0_column.fillOpacity = 0.8;
      level0_column.stroke = am4core.color("#fff");
      level0_column.strokeWidth = 5;
      level0_column.strokeOpacity = 1;

      // by default only current level series bullets are visible, but as we need brand bullets to be visible all the time, we modify it's hidden state
      level0SeriesTemplate.bulletsContainer.hiddenState.properties.opacity = 1;
      level0SeriesTemplate.bulletsContainer.hiddenState.properties.visible = true;


      // level1 series template
      var level1SeriesTemplate = chart.seriesTemplates.create("1");
      level1SeriesTemplate.columns.template.fillOpacity = 0;
      level1SeriesTemplate.columns.template.strokeOpacity = 1;
      let level1_column = level1SeriesTemplate.columns.template;
      level1_column.tooltipText = "{parentName} - {name}: {value}";

      var bullet1 = level1SeriesTemplate.bullets.push(new am4charts.LabelBullet());
      bullet1.locationX = 0.5;
      bullet1.locationY = 0.5;
      bullet1.label.text = "{name}";
      bullet1.label.fill = am4core.color("#ffffff");
      bullet1.label.fontSize = 10;
      bullet1.label.fontFamily = 'Source Sans 3, sans-serif';
      bullet1.label.fontWeight = 'bold';
      bullet1.label.fillOpacity = 1;

      chart.legend = new am4charts.Legend();
      chart.legend.position = "right";
      chart.legend.scrollable = true;
      chart.legend.labels.template.fontSize = 10;
      chart.legend.labels.template.fontFamily = 'Source Sans 3, sans-serif';
      chart.legend.itemContainers.template.paddingTop = 5;
      chart.legend.itemContainers.template.paddingBottom = 5;


      this.chart = chart;

    })
  }

  computeClassBalance() {
    this.classBalance.insights = [];
    const qualifications: string[] = [];
    const target = this.count / this.classBalance.classes.length;
    this.classBalance.classes.forEach((c) => {
      const found = this.data.find((d) => d._id === c)
      if (found) {
        if (found.count < 0.7 * target || found.count > 1.3 * target) {
          qualifications.push('bad');
        } else if (found.count < 0.9 * target || found.count > 1.1 * target) {
          qualifications.push('average');
        } else {
          qualifications.push('good');
        }
      } else {
        qualifications.push('bad');
      }
      if (this.classBalance.insights)
        this.classBalance.insights.push({
          class: c,
          count: found ? found.count : 0,
          percent: (found ? found.count : 0) * 100 / this.count
        })
    })
    if (qualifications.includes('bad')) {
      this.classBalance.qualification = 'bad';
    } else if (qualifications.includes('average')) {
      this.classBalance.qualification = 'average';
    } else {
      this.classBalance.qualification = 'good';
    }
  }
}
